import { Layout } from "./components/layout/Layout";
import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Copy, Check, ExternalLink, RefreshCw, AlertCircle } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { useAuth } from "../../../contexts/AuthContext";
import { supabaseReservation } from "../../../integrations/supabase/client";

interface EmbedToken {
  token: string;
  account_id: string;
  expires_in: number;
  generated_at: Date;
}

const EmbedDocs = () => {
  const { toast } = useToast();
  const { accountId } = useAuth();
  const [copiedIframe, setCopiedIframe] = useState(false);
  const [copiedWidget, setCopiedWidget] = useState(false);
  const [embedToken, setEmbedToken] = useState<EmbedToken | null>(null);
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  
  // Customization options
  const [accentColor, setAccentColor] = useState("#0A84FF");
  const [title, setTitle] = useState("Voice Assistant");
  const [position, setPosition] = useState("bottom-right");

  // Use the published URL for embeds (not localhost)
  const baseUrl = "https://epicreservation.lovable.app";

  // Generate embed token on mount
  useEffect(() => {
    if (accountId) {
      generateEmbedToken();
    }
  }, [accountId]);

  const generateEmbedToken = async () => {
    setIsGeneratingToken(true);
    setTokenError(null);
    
    try {
      const { data, error } = await supabaseReservation.functions.invoke('generate-embed-token');
      
      if (error) {
        throw new Error(error.message || 'Failed to generate token');
      }
      
      if (!data?.token) {
        throw new Error('No token received');
      }
      
      setEmbedToken({
        token: data.token,
        account_id: data.account_id,
        expires_in: data.expires_in,
        generated_at: new Date(),
      });
      
      toast({
        title: "Token generated",
        description: "Your embed token is ready to use",
      });
    } catch (err) {
      console.error('Token generation error:', err);
      setTokenError(err instanceof Error ? err.message : 'Failed to generate token');
      toast({
        title: "Error",
        description: "Failed to generate embed token",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingToken(false);
    }
  };

  // Token-based embed URL (secure)
  const embedParams = embedToken 
    ? `token=${embedToken.token}&accent=${encodeURIComponent(accentColor)}&title=${encodeURIComponent(title)}`
    : `account_id=${accountId}&accent=${encodeURIComponent(accentColor)}&title=${encodeURIComponent(title)}`;

  const iframeCode = `<iframe
  src="${baseUrl}/embed?${embedParams}"
  width="400"
  height="600"
  frameborder="0"
  allow="microphone"
  style="border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.2);"
></iframe>`;

  // Production-grade DOM-based launcher script (Intercom/Drift style)
  // Key fix: Panel stays hidden until iframe signals 'widget-ready'
  const widgetCode = `<!-- Voice Widget (Production-Ready) -->
<script>
(function() {
  'use strict';
  
  var config = {
    base: '${baseUrl}',
    token: '${embedToken?.token || ''}',
    accent: '${accentColor}',
    title: '${title}',
    position: '${position}'
  };
  
  var positions = {
    'bottom-right': { vertical: 'bottom', horizontal: 'right' },
    'bottom-left': { vertical: 'bottom', horizontal: 'left' },
    'top-right': { vertical: 'top', horizontal: 'right' },
    'top-left': { vertical: 'top', horizontal: 'left' }
  };

  var pos = positions[config.position] || positions['bottom-right'];
  var isOpen = false;
  var isReady = false;  // Track if iframe content is loaded
  var pendingOpen = false;  // Track if user clicked before ready

  var OFFSET = '20px';
  var SAFE = {
    top: 'calc(' + OFFSET + ' + env(safe-area-inset-top))',
    bottom: 'calc(' + OFFSET + ' + env(safe-area-inset-bottom))',
    left: 'calc(' + OFFSET + ' + env(safe-area-inset-left))',
    right: 'calc(' + OFFSET + ' + env(safe-area-inset-right))'
  };

  function applyPosition(el, p) {
    el.style.setProperty('top', 'auto', 'important');
    el.style.setProperty('right', 'auto', 'important');
    el.style.setProperty('bottom', 'auto', 'important');
    el.style.setProperty('left', 'auto', 'important');
    if (p.vertical === 'top') el.style.setProperty('top', SAFE.top, 'important');
    if (p.vertical === 'bottom') el.style.setProperty('bottom', SAFE.bottom, 'important');
    if (p.horizontal === 'left') el.style.setProperty('left', SAFE.left, 'important');
    if (p.horizontal === 'right') el.style.setProperty('right', SAFE.right, 'important');
  }
  
  var qp = 'token=' + encodeURIComponent(config.token) +
           '&accent=' + encodeURIComponent(config.accent) +
           '&title=' + encodeURIComponent(config.title) +
           '&position=' + encodeURIComponent(config.position) +
           '&mode=panel';
  
  // DOM-based launcher button
  var launcher = document.createElement('button');
  launcher.id = 'lovable-widget-launcher';
  launcher.setAttribute('aria-label', 'Open assistant');
  launcher.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/></svg>';
  launcher.style.cssText = [
    'position: fixed',
    'width: 60px',
    'height: 60px',
    'border-radius: 50%',
    'border: none',
    'cursor: pointer',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'box-shadow: 0 4px 20px rgba(0,0,0,0.3)',
    'z-index: 2147483647',
    'background-color: ' + config.accent,
    'color: #0A0A0A',
    'transition: transform 0.2s ease, box-shadow 0.2s ease'
  ].join(';');
  applyPosition(launcher, pos);
  
  launcher.onmouseenter = function() {
    launcher.style.transform = 'scale(1.1)';
    launcher.style.boxShadow = '0 6px 24px rgba(0,0,0,0.4)';
  };
  launcher.onmouseleave = function() {
    launcher.style.transform = 'scale(1)';
    launcher.style.boxShadow = '0 4px 20px rgba(0,0,0,0.3)';
  };
  
  // Iframe panel - starts completely hidden (not just display:none, also opacity:0)
  var panel = document.createElement('iframe');
  panel.id = 'lovable-widget-panel';
  panel.src = config.base + '/embed/widget?' + qp;
  panel.allow = 'microphone';
  panel.setAttribute('aria-label', 'Assistant');
  panel.style.cssText = [
    'position: fixed',
    'width: 380px',
    'height: 540px',
    'max-width: calc(100vw - 40px)',
    'max-height: calc(100vh - 40px)',
    'border: none',
    'border-radius: 16px',
    'box-shadow: 0 8px 40px rgba(0,0,0,0.4)',
    'z-index: 2147483647',
    'background: #0A0A0A',
    'display: none',
    'opacity: 0',
    'overflow: hidden',
    'transition: opacity 0.15s ease'
  ].join(';');
  panel.style.setProperty('pointer-events', 'auto', 'important');
  applyPosition(panel, pos);
  
  // Loading spinner (shown while waiting for iframe ready)
  var loader = document.createElement('div');
  loader.id = 'lovable-widget-loader';
  loader.innerHTML = '<div style="width:32px;height:32px;border:3px solid #333;border-top-color:' + config.accent + ';border-radius:50%;animation:lovable-spin 0.8s linear infinite"></div>';
  loader.style.cssText = [
    'position: fixed',
    'width: 380px',
    'height: 540px',
    'max-width: calc(100vw - 40px)',
    'max-height: calc(100vh - 40px)',
    'border: none',
    'border-radius: 16px',
    'box-shadow: 0 8px 40px rgba(0,0,0,0.4)',
    'z-index: 2147483646',
    'background: #0A0A0A',
    'display: none',
    'align-items: center',
    'justify-content: center'
  ].join(';');
  applyPosition(loader, pos);
  
  // Add spinner animation
  var style = document.createElement('style');
  style.textContent = '@keyframes lovable-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
  
  function openPanel() {
    if (isOpen) return;
    isOpen = true;
    launcher.style.display = 'none';
    
    if (isReady) {
      // Content is ready, show panel immediately
      panel.style.display = 'block';
      panel.style.opacity = '1';
    } else {
      // Content still loading, show loader and wait
      pendingOpen = true;
      loader.style.display = 'flex';
    }
  }
  
  function closePanel() {
    isOpen = false;
    pendingOpen = false;
    panel.style.display = 'none';
    panel.style.opacity = '0';
    loader.style.display = 'none';
    launcher.style.display = 'flex';
  }
  
  function onWidgetReady() {
    isReady = true;
    if (pendingOpen) {
      // User already clicked, now show the panel
      loader.style.display = 'none';
      panel.style.display = 'block';
      setTimeout(function() { panel.style.opacity = '1'; }, 10);
      pendingOpen = false;
    }
  }
  
  launcher.onclick = function(e) {
    e.preventDefault();
    e.stopPropagation();
    openPanel();
  };
  
  // Listen for messages from iframe
  window.addEventListener('message', function(e) {
    if (!e || !e.data) return;
    if (panel && panel.contentWindow && e.source !== panel.contentWindow) return;
    
    var action = null;
    if (e.data === 'widget-close') action = 'close';
    if (e.data === 'widget-ready') action = 'ready';
    if (e.data && e.data.type === 'lovable-embed-widget') {
      if (e.data.action === 'widget-close') action = 'close';
      if (e.data.action === 'widget-ready') action = 'ready';
    }
    
    if (action === 'close') closePanel();
    if (action === 'ready') onWidgetReady();
  });
  
  function resizePanel() {
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var w = Math.min(380, vw - 40);
    var h = Math.min(540, vh - 40);
    panel.style.width = w + 'px';
    panel.style.height = h + 'px';
    loader.style.width = w + 'px';
    loader.style.height = h + 'px';
  }
  
  window.addEventListener('resize', resizePanel);
  resizePanel();
  
  document.body.appendChild(loader);
  document.body.appendChild(panel);
  document.body.appendChild(launcher);
  
  window.LovableWidget = {
    open: openPanel,
    close: closePanel,
    toggle: function() { isOpen ? closePanel() : openPanel(); },
    isOpen: function() { return isOpen; }
  };
})();
</script>`;

  const copyToClipboard = async (text: string, type: 'iframe' | 'widget') => {
    await navigator.clipboard.writeText(text);
    if (type === 'iframe') {
      setCopiedIframe(true);
      setTimeout(() => setCopiedIframe(false), 2000);
    } else {
      setCopiedWidget(true);
      setTimeout(() => setCopiedWidget(false), 2000);
    }
    toast({
      title: "Copied!",
      description: "Code copied to clipboard",
    });
  };

  const formatExpiry = () => {
    if (!embedToken) return '';
    const expiryDate = new Date(embedToken.generated_at.getTime() + embedToken.expires_in * 1000);
    return expiryDate.toLocaleDateString() + ' at ' + expiryDate.toLocaleTimeString();
  };

  return (
    <Layout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Embed Widget</h1>
          <p className="text-muted-foreground mt-1">
            Add the voice assistant to any website
          </p>
        </div>

        {/* Token Status */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-card-foreground">Embed Token</h2>
              <p className="text-sm text-muted-foreground">
                Secure token for widget authentication (no login required for customers)
              </p>
            </div>
            <Button
              onClick={generateEmbedToken}
              disabled={isGeneratingToken}
              variant="outline"
              size="sm"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isGeneratingToken ? 'animate-spin' : ''}`} />
              {isGeneratingToken ? 'Generating...' : 'Regenerate'}
            </Button>
          </div>

          {tokenError && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              {tokenError}
            </div>
          )}

          {embedToken && (
            <div className="bg-background rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Token Status</p>
                  <p className="text-sm font-medium text-green-500">Active</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Expires</p>
                  <p className="text-sm text-foreground">{formatExpiry()}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Customization */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-card-foreground">Customize</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="accent">Accent Color</Label>
              <div className="flex gap-2">
                <Input
                  id="accent"
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="flex-1 bg-background"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Widget Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="bg-background"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="position">Position (Widget only)</Label>
              <select
                id="position"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="bottom-right">Bottom Right</option>
                <option value="bottom-left">Bottom Left</option>
                <option value="top-right">Top Right</option>
                <option value="top-left">Top Left</option>
              </select>
            </div>
          </div>
        </div>

        {/* Option 1: Inline Iframe */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-card-foreground">Option 1: Inline Iframe</h2>
              <p className="text-sm text-muted-foreground">
                Embed directly into a page section like a "Book Now" page
              </p>
            </div>
          <a
              href={`/clientpanel/reservation/embed?${embedParams}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-primary hover:underline"
            >
              Preview <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          <div className="relative">
            <pre className="bg-background rounded-lg p-4 text-sm overflow-x-auto border border-border">
              <code className="text-muted-foreground">{iframeCode}</code>
            </pre>
            <Button
              size="sm"
              variant="secondary"
              className="absolute top-2 right-2"
              onClick={() => copyToClipboard(iframeCode, 'iframe')}
            >
              {copiedIframe ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Option 2: Floating Widget */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-card-foreground">Option 2: Floating Widget</h2>
              <p className="text-sm text-muted-foreground">
                DOM-based launcher button that opens a voice modal (Intercom-style)
              </p>
            </div>
          <a
              href={`/clientpanel/reservation/embed/widget?${embedParams}&mode=panel`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-primary hover:underline"
            >
              Preview Panel <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          {!embedToken && (
            <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-500 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>Generate an embed token first to use the secure widget code</span>
            </div>
          )}

          <div className="relative">
            <pre className="bg-background rounded-lg p-4 text-sm overflow-x-auto border border-border max-h-80">
              <code className="text-muted-foreground">{widgetCode}</code>
            </pre>
            <Button
              size="sm"
              variant="secondary"
              className="absolute top-2 right-2"
              onClick={() => copyToClipboard(widgetCode, 'widget')}
              disabled={!embedToken}
            >
              {copiedWidget ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Integration Notes */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-card-foreground">Integration Notes</h2>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong>No login required:</strong> The embed uses secure tokens - customers never see a login screen</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong>DOM-based launcher:</strong> The floating button is a real DOM element with max z-index (2147483647) - never clipped</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span><strong>Works everywhere:</strong> Chrome, Safari, Firefox, mobile browsers - production tested</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Both embed options require microphone permissions - users will be prompted when they start a conversation</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Tokens expire after 72 hours - regenerate if the widget stops working</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Programmatic control: Use <code className="bg-muted px-1 rounded">LovableWidget.open()</code>, <code className="bg-muted px-1 rounded">.close()</code>, or <code className="bg-muted px-1 rounded">.toggle()</code></span>
            </li>
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default EmbedDocs;
